import { useEffect } from 'react'
import { Edge, Node, Position, useNodesInitialized, useReactFlow } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { NODE_HEIGHT, NODE_WIDTH } from '@ui/constants/pipelinesWorkflow'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { useNodeAndEdgeStore } from '@ui/stores'
import { HierarchyPointNode, stratify, tree } from 'd3-hierarchy'

// the layout direction (T = top, R = right, B = bottom, L = left, TB = top to bottom, ...)
export type Direction = 'TB' | 'LR' | 'RL' | 'BT'

const direction: Direction = 'TB'

const positionMap: Record<string, Position> = {
  T: Position.Top,
  L: Position.Left,
  R: Position.Right,
  B: Position.Bottom,
}

const getPosition = (x: number, y: number, direction: Direction) => {
  switch (direction) {
    case 'LR':
      return { x: y, y: x }
    case 'RL':
      return { x: -y, y: -x }
    case 'BT':
      return { x: -x, y: -y }
    default:
      return { x, y }
  }
}

// initialize the tree layout (see https://observablehq.com/@d3/tree for examples)
const layout = tree<Node>()
  // the node size configures the spacing between the nodes ([width, height])
  .nodeSize([NODE_HEIGHT * 1.25, NODE_WIDTH * 1.75])
  // this is needed for creating equal space between all nodes
  .separation(() => 1)

const layoutV2 = tree<Node>()
  // the node size configures the spacing between the nodes ([width, height])
  .nodeSize([100 * 3, 240 * 1.25])
  // this is needed for creating equal space between all nodes
  .separation(() => 1)

function useAutoLayout() {
  const workflowUpdateEnabled = useFeatureFlag(FeatureFlagName.WorkflowUpdate)

  const nodesInitialized = useNodesInitialized()

  const { refreshLayout, setRefreshLayout, setNodes, setEdges } = useNodeAndEdgeStore(
    useShallow((state) => ({
      refreshLayout: state.refreshLayout,
      setRefreshLayout: state.setRefreshLayout,
      setNodes: state.setNodes,
      setEdges: state.setEdges,
    }))
  )

  const nodeLayout = workflowUpdateEnabled ? layoutV2 : layout

  const { getNodes, getEdges, setNodes: setFlowNodes, setEdges: setFlowEdges } = useReactFlow()

  useEffect(() => {
    // only run the layout if there are nodes and they have been initialized with their dimensions
    if (!nodesInitialized) {
      return
    }

    const nodes: Node[] = getNodes()
    const edges: Edge[] = getEdges()

    // Tired of not being able to debug through no root error that is thrown
    // because of stratify
    try {
      const hierarchy = stratify<Node>()
        .id((d) => d.id)
        // get the id of each node by searching through the edges
        // this only works if every node has one connection
        .parentId((d: Node) => edges.find((e: Edge) => e.target === d.id)?.source)(nodes)

      // run the layout algorithm with the hierarchy data structure
      const root = nodeLayout(hierarchy)

      // We need to update the position of the nodes and edges in the store as well as React Flow
      // or else layout becomes out of sync with the actual positions and static
      const updateNodePositions = (
        nodes: Node[],
        root: HierarchyPointNode<Node>,
        positionMap: Record<string, Position>,
        direction: Direction
      ) => {
        return nodes.map((node) => {
          // find the node in the hierarchy with the same id and get its coordinates
          const { x, y } = root.find((d) => d.id === node.id) || { x: node.position.x, y: node.position.y }

          return {
            ...node,
            sourcePosition: positionMap[direction[1]],
            targetPosition: positionMap[direction[0]],
            position: getPosition(x, y, direction),
          }
        })
      }

      setNodes((nodes) => updateNodePositions(nodes, root, positionMap, direction))
      setFlowNodes((nodes) => updateNodePositions(nodes, root, positionMap, direction))

      setEdges((edges: Edge[]) => edges.map((edge) => ({ ...edge })))
      setFlowEdges((edges: Edge[]) => edges.map((edge) => ({ ...edge })))

      setRefreshLayout(false)
    } catch (error) {
      console.error('Error setting up pipeline:', error)
    }
  }, [
    refreshLayout,
    nodesInitialized,
    setRefreshLayout,
    getNodes,
    getEdges,
    setNodes,
    setEdges,
    setFlowNodes,
    setFlowEdges,
    nodeLayout,
  ])
}

export default useAutoLayout
